import classnames from "classnames";
import * as React from "react";
import AccordionItem, { type AccordionItemPropsInner } from "./Item";
import { getActiveItemIndices } from "./lib";

import "./style.less";

export enum AccordionMode {
  Accordion = "Accordion",
  Static = "Static",
  Toggle = "Toggle"
}

export enum AutoExpandItems {
  None = "None",
  First = "First",
  All = "All"
}

export interface AccordionProps {
  activeItemIndex?: number | null;
  arrow?: "hidden" | "center";
  autoExpand?: AutoExpandItems;
  children?: any;
  className?: string;
  hasGap?: boolean;
  hideIfEmpty?: boolean;
  isLazy?: boolean;
  mode?: AccordionMode;
  noAnimation?: boolean;
  onItemActivated?: (index: number | null) => void;
  onItemClick?: (index: number) => void;
  style?: React.CSSProperties;
  theme?: "default" | "advanced" | "tile";
  useHash?: boolean;
}

const Accordion = ({
  autoExpand = AutoExpandItems.First,
  mode = AccordionMode.Accordion,
  ...props
}: AccordionProps) => {
  const [activeItemIndices, setActiveItemIndices] = React.useState<number[]>([]);

  React.useEffect(
    () =>
      setActiveItemIndices(
        getActiveItemIndices({
          activeItemIndex: props.activeItemIndex,
          autoExpand: autoExpand,
          children: props.children,
          useHash: props.useHash
        })
      ),
    [props.useHash, autoExpand, mode, props.activeItemIndex]
  );

  const getOnItemClick = React.useCallback(
    (index: number, onClick?: (isActive: boolean) => void) => () => {
      if (props.onItemClick) {
        props.onItemClick(index);
      } else {
        let newActiveItemIndices: number[] = [];
        switch (mode) {
          case AccordionMode.Accordion:
            newActiveItemIndices = activeItemIndices[0] === index ? [] : [index];
            break;
          case AccordionMode.Toggle:
            newActiveItemIndices = activeItemIndices.slice();
            const i = newActiveItemIndices.indexOf(index);
            if (i >= 0) {
              newActiveItemIndices.splice(i, 1);
            } else {
              newActiveItemIndices.push(index);
            }
            break;
        }
        const isActive = newActiveItemIndices.includes(index);
        onClick && onClick(isActive);

        if (mode !== AccordionMode.Static) {
          props.onItemActivated && props.onItemActivated(isActive ? index : null);
        }

        setActiveItemIndices(newActiveItemIndices);
      }
    },
    [props.onItemClick, activeItemIndices]
  );

  return (
    <div
      className={classnames(
        "accordion",
        props.arrow ? `arrow_${props.arrow}` : undefined,
        props.className,
        props.theme ? `theme_${props.theme}` : undefined,
        {
          hide_empty: props.hideIfEmpty,
          has_gap: props.hasGap
        }
      )}
      style={props.style}
    >
      {React.Children.map<React.ReactElement<any>, React.ReactElement<AccordionItemPropsInner>>(
        props.children,
        (child, index) =>
          React.cloneElement(child, {
            ...child.props,
            className: classnames(child.props.className, {
              clickable: props.onItemClick || child.props.onClick
            }),
            key: index,
            isActive: activeItemIndices.indexOf(index) >= 0,
            noAnimation: props.noAnimation || child.props.noAnimation,
            onClick: getOnItemClick(index, child.props.onClick),
            isLazy: props.isLazy
          })
      )}
    </div>
  );
};
Accordion.Item = AccordionItem;
export default Accordion;
